<template>
  <div class="upgrade-container" v-if="!contentLoading">
    <div class="upgrade-managed-contents">
      <img :src="headerImage" alt="Output One banner" class="header-image" />
      <ScreenHeading>{{ title }}</ScreenHeading>
      <div v-for="({ heading, list }, index) in contentSections" :key="index" class="list-item">
        <h3>{{ heading }}</h3>
        <ul>
          <li v-for="listItem in list" :key="listItem">{{ listItem }}</li>
        </ul>
      </div>
    </div>
    <PaymentDetails />
    <RecurlyTokenErrors />
    <Recurly3DSContainer />
    <div class="upgrade-footer">
      <PlanSummaryCardContainer />
      <p class="fineprint">
        By clicking {{ buttonText }} you agree to Output’s <a :href="termsUrl" target="_blank">Terms of Use</a>,
        <a :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>, and that you will automatically be charged the membership fee ({{
          selectedPlanDisplayPrice
        }}/{{ selectedPlanInterval }} pre-tax) until you cancel. You may cancel anytime by going to your Output Account and clicking "Cancel Subscription."
      </p>
      <ScreenNextSectionBtn :text="buttonText" hide-enter @navigateClick="submitUpgrade" class="submit-button" :disabled="!hasValidPaymentDetails" />
      <img class="icons" src="https://storyblok-assets.output.com/896x80/10a4a3e8bf/trust-badges.png" width="448" height="40" alt="" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAmplitude, BrazeEvents } from '@outputinc/o-analytics'
import ScreenHeading from '@/components/shared/screens/ScreenHeading.vue'
import ScreenNextSectionBtn from '@/components/shared/screens/ScreenNextSectionBtn.vue'
import PaymentDetails from '@/components/shared/payment-details/PaymentDetails.vue'
import PlanSummaryCardContainer from '@/components/shared/plan-summary/PlanSummaryCardContainer.vue'
import Recurly3DSContainer from '@/components/shared/recurly/Recurly3DSContainer.vue'
import RecurlyTokenErrors from '@/components/shared/recurly/RecurlyTokenErrors.vue'
import { useBrazeStore } from '@/store/BrazeStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useModalStore } from '@/store/ModalStore'
import { useRecurlyStore } from '@/store/RecurlyStore'
import { useScreenStore } from '@/store/ScreenStore'
import { useUserStore } from '@/store/UserStore'
import { usePlanStore } from '@/store/PlanStore'
import { usePriceStore } from '@/store/PriceStore'
import { useContentStore } from '@/store/ContentStore'
import { Error } from '@/types'

const recurlyStore = useRecurlyStore()
const userStore = useUserStore()
const checkoutStore = useCheckoutStore()
const planStore = usePlanStore()
const priceStore = usePriceStore()

const { handleTransactionError, submitChangeSubscription, onChangeSubscription, process3DSecure, onBillingInfoUpdate } = recurlyStore
const { setCheckoutProperty, clear3DSToken } = checkoutStore
const { paymentDetails } = storeToRefs(checkoutStore)
const { trigger: triggerModal } = useModalStore()
const { outputUid } = storeToRefs(userStore)

const { trackEmailUser } = useBrazeStore()
const { setScreenComplete, navigateNext, setBusy } = useScreenStore()
const { track, Events } = useAmplitude()

const { selectedPlanProduct, selectedPlanInterval } = storeToRefs(planStore)

const { selectedPlanDisplayPrice } = storeToRefs(priceStore)
const contentStore = useContentStore()
const { headerImage, headerTitle: title, contentSections, ctaText: buttonText, loading: contentLoading } = storeToRefs(contentStore)

defineProps<{
  submitHandler: () => void
}>()

const isSubmitting = ref(false)

const hasValidPaymentDetails = computed(() => !!paymentDetails.value)

const termsUrl = `${import.meta.env.VITE_OUTPUT_DOT_COM_HOSTNAME}/legal/terms`
const privacyPolicyUrl = `${import.meta.env.VITE_OUTPUT_DOT_COM_HOSTNAME}/legal/privacy`

const handleError = (error: Error) => {
  if (error.type === 'transaction' && error.transactionError?.code === 'three_d_secure_action_required') {
    const threeDSecure = process3DSecure(error.transactionError.threeDSecureActionTokenId, submitChangeSubscription)

    threeDSecure.attach(document.querySelector('#recurly-3ds-container'))
  } else {
    triggerModal({ error })
    handleTransactionError()
  }
}

onBillingInfoUpdate((response: any) => {
  if (!response?.data) {
    return
  }

  const {
    data: { setSubscriptionBillingInfo }
  } = response

  const { error } = setSubscriptionBillingInfo

  isSubmitting.value = false

  if (error) {
    setBusy(false)
    clear3DSToken()
    handleError(error)
  }
})

onChangeSubscription((subscriptionResponse: any) => {
  if (!subscriptionResponse?.data) {
    return
  }

  const {
    data: { changeSubscription }
  } = subscriptionResponse

  const { error } = changeSubscription

  isSubmitting.value = false

  if (error) {
    clear3DSToken()
    setBusy(false)
    handleError(error)
  }

  if (!error && !!changeSubscription?.id) {
    const { plan } = changeSubscription

    track(Events.CHECKOUT_SUBSCRIPTION_UPGRADE_COMPLETE, {
      planCodeSelected: plan.code,
      products: [selectedPlanProduct.value]
    })

    trackEmailUser({
      externalId: outputUid.value,
      name: BrazeEvents.CHECKOUT_SUBSCRIPTION_UPGRADE_COMPLETE,
      time: new Date().toISOString(),
      properties: {
        plan_code: plan.code,
        products: selectedPlanProduct.value
      }
    })

    setScreenComplete(true)

    setCheckoutProperty({ isComplete: true })
    navigateNext()
  }
})

const submitUpgrade = () => {
  if (isSubmitting.value) {
    return // prevent double submission
  }
  isSubmitting.value = true

  submitChangeSubscription()
}
</script>

<style scoped lang="scss">
.hidden {
  display: none;
}

.upgrade-container {
  max-width: 345px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: $spacer-3 auto;
  padding: $spacer-3;

  .screen-next-section-btn {
    width: 100%;
  }

  @include media-breakpoint(sm) {
    max-width: 576px;
    margin: $spacer-6 auto;
    padding: $spacer-5;
  }

  .header-image {
    max-width: 100%;
    margin-bottom: $spacer-4;

    @include media-breakpoint(sm) {
      margin-bottom: $spacer-6;
    }
  }

  .list-item {
    width: 100%;
  }

  .plan-info {
    text-align: center;
    margin: $spacer-4 0;

    p {
      margin-bottom: 0;
    }

    .subheading {
      font-family: $font-family-pilat-regular;
    }
  }

  .upgrade-footer {
    display: flex;
    flex-direction: column;
    align-items: center;

    .fineprint {
      margin-bottom: 0;
      font-size: 10px;
      line-height: $spacer-3;
      letter-spacing: 0.4px;
    }

    .submit-button {
      width: 100%;
    }

    .icons {
      margin-top: $spacer-5;

      @include media-breakpoint(sm) {
        margin-top: $spacer-6;
      }
    }
  }
}
</style>
